import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { getEvents, submitEvent, eventSponsorCheckout } from "../lib/api";
import { fmtEventDate, fmtEventTime, monthKey } from "../lib/eventFormat";
import { CalendarDays, MapPin, Star, Plus, X, List, Grid3x3, Sparkles } from "lucide-react";
import ImageUpload from "../components/ImageUpload";
import SectionSponsor from "../components/SectionSponsor";

const CATEGORIES = ["Community", "Festival", "Music", "Sports", "Arts", "Food & Drink", "Civic", "Family"];
const inputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[var(--gold)] bg-white";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [view, setView] = useState("list");
  const [monthDate, setMonthDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => getEvents().then(setEvents).catch(() => {});
  useEffect(() => { load(); }, []);

  const sorted = useMemo(() => [...events].sort((a, b) => (a.start || "").localeCompare(b.start || "")), [events]);
  const sponsored = sorted.filter((e) => e.sponsored);
  const listShown = selectedDay
    ? sorted.filter((e) => (e.start || "").slice(0, 10) === selectedDay)
    : sorted;

  return (
    <div className="paper-texture min-h-screen">
      <div className="mx-auto max-w-7xl px-4 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-[var(--navy)] pb-4">
          <div>
            <p className="eyebrow text-[var(--gold)]">Amelia Island · Nassau County</p>
            <h1 className="font-display text-4xl font-bold text-[var(--navy)] sm:text-5xl">Local Events Calendar</h1>
            <p className="font-serif-body mt-1 text-slate-600">Festivals, games, markets and community happenings across the island.</p>
            <div className="mt-3"><SectionSponsor section="events" /></div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex overflow-hidden rounded-lg border border-slate-300">
              <button data-testid="events-view-list" onClick={() => { setView("list"); setSelectedDay(null); }} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold ${view === "list" ? "bg-[var(--navy)] text-white" : "bg-white text-slate-600"}`}><List size={14} /> List</button>
              <button data-testid="events-view-month" onClick={() => setView("month")} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold ${view === "month" ? "bg-[var(--navy)] text-white" : "bg-white text-slate-600"}`}><Grid3x3 size={14} /> Month</button>
            </div>
            <button data-testid="submit-event-btn" onClick={() => setOpen(true)} className="flex items-center gap-2 rounded-full bg-[var(--gold)] px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#b08f48]">
              <Plus size={15} /> Submit Event
            </button>
          </div>
        </div>

        {sponsored.length > 0 && (
          <div className="mt-6">
            <p className="eyebrow mb-2 text-[var(--gold)]">Featured Events</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {sponsored.slice(0, 3).map((e) => <EventCard key={e.id} e={e} featured />)}
            </div>
          </div>
        )}

        {view === "month" ? (
          <MonthGrid events={sorted} monthDate={monthDate} setMonthDate={setMonthDate} selectedDay={selectedDay} setSelectedDay={setSelectedDay} />
        ) : null}

        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="section-rule font-display pt-2 text-lg font-bold text-[var(--navy)]">
              {selectedDay ? `Events on ${fmtEventDate(selectedDay)}` : "Upcoming Events"}
            </h2>
            {selectedDay && <button onClick={() => setSelectedDay(null)} className="text-xs font-semibold text-[var(--gold)]">Clear filter ✕</button>}
          </div>
          {listShown.length === 0 ? (
            <p className="font-serif-body py-10 text-center text-slate-500">No events to show. Be the first to submit one!</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="events-list">
              {listShown.map((e) => <EventCard key={e.id} e={e} />)}
            </div>
          )}
        </div>
      </div>

      {open && <SubmitEventModal onClose={() => setOpen(false)} onDone={load} />}
    </div>
  );
}

function EventCard({ e, featured }) {
  return (
    <div data-testid={`event-card-${e.id}`} className={`card-hover overflow-hidden rounded-xl border bg-white ${featured ? "border-[var(--gold)] ring-1 ring-[var(--gold)]/30" : "border-[var(--paper-border)]"}`}>
      {e.image && <div className="img-zoom h-40 overflow-hidden"><img src={e.image} alt={e.title} className="h-full w-full object-cover" /></div>}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <span className="eyebrow text-[var(--gold)]">{e.category}</span>
          {e.sponsored && <span className="inline-flex items-center gap-1 rounded-full bg-[var(--gold)] px-2 py-0.5 text-[10px] font-bold uppercase text-white"><Star size={10} /> Featured</span>}
        </div>
        <h3 className="font-display mt-1 text-lg font-bold text-[var(--navy)]">{e.title}</h3>
        <p className="font-ui mt-1 flex items-center gap-1.5 text-xs font-semibold text-slate-700"><CalendarDays size={13} className="text-[var(--navy)]" /> {fmtEventDate(e.start)}{fmtEventTime(e.start) && ` · ${fmtEventTime(e.start)}`}</p>
        {e.location && <p className="font-ui mt-0.5 flex items-center gap-1.5 text-xs text-slate-500"><MapPin size={13} /> {e.location}</p>}
        {e.description && <p className="font-serif-body mt-2 line-clamp-3 text-sm text-slate-600">{e.description}</p>}
      </div>
    </div>
  );
}

function MonthGrid({ events, monthDate, setMonthDate, selectedDay, setSelectedDay }) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const first = new Date(year, month, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const mk = monthKey(monthDate);
  const byDay = {};
  events.forEach((e) => {
    const d = (e.start || "").slice(0, 10);
    if (d.startsWith(mk)) (byDay[d] = byDay[d] || []).push(e);
  });
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const label = monthDate.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="mt-6 rounded-xl border border-[var(--paper-border)] bg-white p-4" data-testid="events-month-grid">
      <div className="mb-3 flex items-center justify-between">
        <button onClick={() => setMonthDate(new Date(year, month - 1, 1))} className="rounded-lg border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-600 hover:bg-slate-50">‹ Prev</button>
        <h3 className="font-display text-xl font-bold text-[var(--navy)]">{label}</h3>
        <button onClick={() => setMonthDate(new Date(year, month + 1, 1))} className="rounded-lg border border-slate-300 px-3 py-1 text-sm font-semibold text-slate-600 hover:bg-slate-50">Next ›</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="font-ui py-1 text-[11px] font-bold uppercase tracking-wide text-slate-400">{d}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const key = `${mk}-${String(d).padStart(2, "0")}`;
          const dayEvents = byDay[key] || [];
          const active = selectedDay === key;
          return (
            <button
              key={key}
              data-testid={`cal-day-${d}`}
              onClick={() => dayEvents.length && setSelectedDay(active ? null : key)}
              className={`min-h-[64px] rounded-lg border p-1.5 text-left transition-colors ${active ? "border-[var(--gold)] bg-amber-50" : "border-slate-100 hover:bg-slate-50"} ${dayEvents.length ? "cursor-pointer" : "cursor-default"}`}
            >
              <span className="font-ui text-xs font-bold text-slate-600">{d}</span>
              <div className="mt-1 space-y-0.5">
                {dayEvents.slice(0, 2).map((e) => (
                  <span key={e.id} className={`block truncate rounded px-1 py-0.5 text-[9px] font-semibold ${e.sponsored ? "bg-[var(--gold)] text-white" : "bg-[var(--navy)]/10 text-[var(--navy)]"}`}>{e.title}</span>
                ))}
                {dayEvents.length > 2 && <span className="block text-[9px] text-slate-400">+{dayEvents.length - 2} more</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SubmitEventModal({ onClose, onDone }) {
  const empty = { title: "", category: "Community", start: "", end: "", location: "", description: "", image: "", contact_name: "", contact_email: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [promote, setPromote] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.start) return toast.error("Event title and date are required.");
    setBusy(true);
    try {
      const res = await submitEvent(form);
      if (promote && res.id) {
        const { checkout_url } = await eventSponsorCheckout(res.id);
        window.location.href = checkout_url;
        return;
      }
      toast.success("Thanks! Your event is pending editor approval.");
      onClose(); onDone();
    } catch {
      toast.error("Could not submit event. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" onClick={onClose}>
      <form onClick={(ev) => ev.stopPropagation()} onSubmit={submit} data-testid="event-submit-form" className="my-8 w-full max-w-2xl rounded-xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-xl font-bold text-[var(--navy)]">Submit a Community Event</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={22} /></button>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2"><span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Event title</span>
            <input data-testid="ev-title" value={form.title} onChange={set("title")} className={inputCls} placeholder="Amelia Island Jazz Festival" /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Category</span>
            <select data-testid="ev-category" value={form.category} onChange={set("category")} className={inputCls}>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Location</span>
            <input data-testid="ev-location" value={form.location} onChange={set("location")} className={inputCls} placeholder="Centre St, Fernandina Beach" /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Starts</span>
            <input data-testid="ev-start" type="datetime-local" value={form.start} onChange={set("start")} className={inputCls} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Ends (optional)</span>
            <input data-testid="ev-end" type="datetime-local" value={form.end} onChange={set("end")} className={inputCls} /></label>
          <label className="block sm:col-span-2"><span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Description</span>
            <textarea data-testid="ev-desc" value={form.description} onChange={set("description")} rows={3} className={inputCls} /></label>
          <div className="sm:col-span-2"><span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Event photo (optional)</span>
            <ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testid="ev-image" /></div>
          <div className="grid grid-cols-2 gap-4 sm:col-span-2">
            <label className="block"><span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Your name</span>
              <input data-testid="ev-contact-name" value={form.contact_name} onChange={set("contact_name")} className={inputCls} /></label>
            <label className="block"><span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Contact email</span>
              <input data-testid="ev-contact-email" type="email" value={form.contact_email} onChange={set("contact_email")} className={inputCls} /></label>
          </div>
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[var(--gold)]/50 bg-amber-50/60 p-4 sm:col-span-2" data-testid="ev-promote">
            <input type="checkbox" checked={promote} onChange={(e) => setPromote(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[var(--gold)]" />
            <span>
              <span className="flex items-center gap-1.5 font-semibold text-[var(--navy)]"><Sparkles size={15} className="text-[var(--gold)]" /> Promote this event — $25</span>
              <span className="mt-0.5 block text-xs text-slate-600">Featured placement with a gold badge at the top of the calendar. You'll be taken to secure checkout, and your event goes live immediately after payment.</span>
            </span>
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={busy} data-testid="ev-submit" className="rounded-lg bg-[var(--navy)] px-5 py-2 text-sm font-semibold text-white hover:bg-[var(--header-navy)] disabled:opacity-60">
            {busy ? "Submitting…" : promote ? "Continue to payment" : "Submit for approval"}
          </button>
        </div>
      </form>
    </div>
  );
}
